import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import type { TenantParty } from '@/api/tenantParties';

interface Props {
  party: TenantParty | null;
  onClose: () => void;
}

const TYPE_COLOR: Record<string, string> = {
  RETAILER:       '#C17BAF',
  WHOLESALER:     '#7B61FF',
  DISTRIBUTOR:    '#7B61FF',
  REPRESENTATIVE: '#FF6F61',
  OTHERS:         '#9CA3AF',
};

export function TenantPartyDetailDialog({ party, onClose }: Props) {
  if (!party) return null;

  const rows: { label: string; value?: string | number | null }[] = [
    { label: 'Party Code',   value: party.partyCode },
    { label: 'Type',         value: party.partyType },
    { label: 'Billing',      value: party.defaultPricing },
    { label: 'Contact',      value: party.contactPerson },
    { label: 'Phone',        value: party.mobile ?? party.phone },
    { label: 'Email',        value: party.email },
    { label: 'GSTIN',        value: party.gstin },
    { label: 'DL Number',    value: party.dlNumber },
    { label: 'DL Expiry',    value: party.dlExpiry },
    { label: 'PAN',          value: party.panNumber },
    { label: 'Sales Rep',    value: party.repName },
    { label: 'Address',      value: party.addressLine1 ?? party.address },
    { label: 'Address 2',    value: party.addressLine2 },
    { label: 'Area',         value: party.area },
    { label: 'City',         value: party.city },
    { label: 'Pincode',      value: party.pincode },
    { label: 'Outstanding',  value: party.outstanding != null ? `₹${Number(party.outstanding).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : null },
    { label: 'Credit Limit', value: (party as any).creditLimit != null ? `₹${(party as any).creditLimit}` : null },
    { label: 'Credit Days',  value: (party as any).creditDays  != null ? `${(party as any).creditDays} days` : null },
  ].filter(r => r.value != null && r.value !== '');

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth onClick={e => e.stopPropagation()}>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1, pr: 10, display: 'flex', alignItems: 'center', gap: 1 }}>
        {party.name}
        {party.partyType && (
          <Box component="span" sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, whiteSpace: 'nowrap', flexShrink: 0, color: TYPE_COLOR[party.partyType] ?? TYPE_COLOR.OTHERS }}>
            {party.partyType}
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>✕</IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {rows.map(r => (
          <Box key={r.label} sx={{ display: 'flex', gap: 2, py: 0.6, borderBottom: '1px solid #f3f4f6' }}>
            <Typography sx={{ fontSize: 12, color: '#6b7280', width: 110, flexShrink: 0, fontWeight: 600 }}>{r.label}</Typography>
            <Typography sx={{ fontSize: 13, color: r.label === 'Outstanding' ? '#dc2626' : '#111827', fontWeight: r.label === 'Outstanding' ? 700 : 400, wordBreak: 'break-all' }}>
              {String(r.value)}
            </Typography>
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button size="small" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
